import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "./api";
import { useAuth } from "./AuthContext";
import heroVideo from "../assets/dgb-hero.mp4";
import { PALETTE, GLOW, MOTION } from "../design-tools/tokens";

/* ─────────────────────────────────────────────
   Blueprint corner glyph
───────────────────────────────────────────── */
function Corner({ position = "tl", size = 28 }) {
  const flipX = position.includes("r");
  const flipY = position.includes("b");
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      style={{ transform: `scale(${flipX ? -1 : 1}, ${flipY ? -1 : 1})` }}
    >
      <line x1="0" y1="0" x2={size * 0.65} y2="0" stroke={PALETTE.teal700} strokeWidth="0.8" opacity="0.7" />
      <line x1="0" y1="0" x2="0" y2={size * 0.65} stroke={PALETTE.teal700} strokeWidth="0.8" opacity="0.7" />
      <circle cx={size * 0.2} cy={size * 0.2} r="2" fill="none" stroke={PALETTE.teal700} strokeWidth="0.5" opacity="0.6" />
    </svg>
  );
}

/* ─────────────────────────────────────────────
   Reusable field block
───────────────────────────────────────────── */
function Field({ label, children }) {
  return (
    <div>
      <label style={F.label}>{label}</label>
      {children}
    </div>
  );
}

export default function Signup() {
  const { refresh } = useAuth();
  const navigate    = useNavigate();

  const [form, setForm] = useState({
    firstName: "", lastName: "", organization: "", role: "",
    dateOfBirth: "", email: "", phone: "", password: "",
  });
  const [err,  setErr]  = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await api.post("/auth/signup", form);
      await refresh();
      navigate("/dashboard", { replace: true });
    } catch (ex) {
      setErr(ex?.response?.data?.error || "Signup failed.");
    } finally {
      setBusy(false);
    }
  };

  const inputProps = {
    style: F.input,
    onFocus: (e) => (e.target.style.borderColor = PALETTE.teal600),
    onBlur:  (e) => (e.target.style.borderColor = "rgba(53,143,128,0.25)"),
  };

  return (
    <div style={F.root}>
      {/* ── Video background ── */}
      <video autoPlay muted loop playsInline style={F.video}>
        <source src={heroVideo} type="video/mp4" />
      </video>

      {/* ── Gradient overlay ── */}
      <div aria-hidden="true" style={F.overlay} />

      {/* ── Blueprint corners ── */}
      <div style={{ position: "fixed", top: 28, left: 28, zIndex: 3 }}><Corner position="tl" /></div>
      <div style={{ position: "fixed", top: 28, right: 28, zIndex: 3 }}><Corner position="tr" /></div>
      <div style={{ position: "fixed", bottom: 28, left: 28, zIndex: 3 }}><Corner position="bl" /></div>
      <div style={{ position: "fixed", bottom: 28, right: 28, zIndex: 3 }}><Corner position="br" /></div>

      {/* ── Scan line ── */}
      <div aria-hidden="true" style={F.scanLine} />

      {/* ── Telemetry ── */}
      <div style={F.telemTL}>
        <div>N 40°43′ &nbsp; W 74°00′</div>
        <div style={{ opacity: 0.5 }}>◌ &nbsp; ATELIER 01 &nbsp; ◌ &nbsp; NEW ACCOUNT</div>
      </div>

      {/* ── Schematic deco ── */}
      <div aria-hidden="true" style={F.deco}>
        <svg width="220" height="220" viewBox="0 0 220 220">
          {[0, 1, 2].map((i) => (
            <line key={`h${i}`} x1="0" y1={i * 110} x2="220" y2={i * 110}
              stroke="#36c4a8" strokeWidth="0.4" strokeDasharray="4 8" />
          ))}
          {[0, 1, 2].map((i) => (
            <line key={`v${i}`} x1={i * 110} y1="0" x2={i * 110} y2="220"
              stroke="#36c4a8" strokeWidth="0.4" strokeDasharray="4 8" />
          ))}
          <circle cx="110" cy="110" r="72" fill="none" stroke="#36c4a8" strokeWidth="0.4" strokeDasharray="3 6" />
          <circle cx="110" cy="110" r="3" fill="#36c4a8" />
        </svg>
      </div>

      {/* ── Card ── */}
      <div style={F.card}>
        {/* Top accent line */}
        <div aria-hidden="true" style={F.cardTopLine} />

        <p style={F.eyebrow}>◈ &nbsp; START THE CONVERSATION</p>

        <h1 style={F.h1}>
          Create your{" "}
          <em style={{ fontStyle: "italic", color: PALETTE.teal400, textShadow: GLOW.text }}>
            account.
          </em>
        </h1>

        <p style={F.lede}>
          A few details to get you set up. Your information stays private —
          we'll only use it to communicate with you about your projects.
        </p>

        <div style={F.divider} />

        <form onSubmit={submit} noValidate>
          {/* Name row */}
          <div style={F.row}>
            <Field label="First name">
              <input {...inputProps} value={form.firstName} onChange={set("firstName")} required />
            </Field>
            <Field label="Last name">
              <input {...inputProps} value={form.lastName} onChange={set("lastName")} required />
            </Field>
          </div>

          {/* Org / role row */}
          <div style={F.row}>
            <Field label="Organization (optional)">
              <input {...inputProps} value={form.organization} onChange={set("organization")} />
            </Field>
            <Field label="Role (optional)">
              <input {...inputProps} value={form.role} onChange={set("role")} />
            </Field>
          </div>

          {/* DOB / phone row */}
          <div style={F.row}>
            <Field label="Date of birth">
              <input {...inputProps} type="date" value={form.dateOfBirth} onChange={set("dateOfBirth")} required />
            </Field>
            <Field label="Phone">
              <input {...inputProps} type="tel" value={form.phone} onChange={set("phone")} required />
            </Field>
          </div>

          {/* Full-width fields */}
          <Field label="Email address">
            <input {...inputProps} type="email" value={form.email} onChange={set("email")} autoComplete="email" required />
          </Field>

          <div style={{ marginTop: 16 }}>
            <Field label="Password (8+ characters)">
              <input {...inputProps} type="password" value={form.password} onChange={set("password")} autoComplete="new-password" minLength={8} required />
            </Field>
          </div>

          {err && (
            <div style={F.err}>
              <span style={{ color: PALETTE.teal500 }}>◌</span> {err}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            style={F.btn}
            onMouseEnter={(e) => {
              if (!busy) {
                e.currentTarget.style.background = PALETTE.bgElevated;
                e.currentTarget.style.borderColor = PALETTE.teal600;
                e.currentTarget.style.letterSpacing = "0.22em";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(54,196,168,0.06)";
              e.currentTarget.style.borderColor = "rgba(53,143,128,0.3)";
              e.currentTarget.style.letterSpacing = "0.18em";
            }}
          >
            <span>{busy ? "Creating account…" : "Create account"}</span>
            <span aria-hidden="true">→</span>
          </button>
        </form>

        <div style={F.altRow}>
          <Link to="/" style={F.altLink}
            onMouseEnter={(e) => (e.target.style.color = PALETTE.teal400)}
            onMouseLeave={(e) => (e.target.style.color = PALETTE.teal700)}>
            ← Back to site
          </Link>
          <Link to="/login" style={F.altLink}
            onMouseEnter={(e) => (e.target.style.color = PALETTE.teal400)}
            onMouseLeave={(e) => (e.target.style.color = PALETTE.teal700)}>
            Have an account? Sign in →
          </Link>
        </div>

        <p style={F.cardFooter}>DBG &nbsp; ◌ &nbsp; SECURE SIGNUP &nbsp; ◌ &nbsp; MMXXVI</p>
      </div>

      {/* ── Bottom telemetry ── */}
      <div style={F.telemBottom}>
        SYS:READY &nbsp; ◌ &nbsp; AUTH NODE &nbsp; ◌ &nbsp; GLOBAL
      </div>
    </div>
  );
}

/* ─── Styles ──────────────────────────────── */
const F = {
  root: {
    minHeight: "100svh",
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "80px 24px",
    background: PALETTE.ink,
    overflow: "hidden",
  },

  video: {
    position: "fixed",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    objectPosition: "center",
    opacity: 0.18,
    zIndex: 0,
    pointerEvents: "none",
  },

  overlay: {
    position: "fixed",
    inset: 0,
    zIndex: 1,
    pointerEvents: "none",
    background: `radial-gradient(ellipse at 35% 40%, rgba(54,196,168,0.07), transparent 55%),
                 linear-gradient(180deg, rgba(10,18,16,0.65) 0%, rgba(10,18,16,0.9) 100%)`,
  },

  scanLine: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    height: "1px",
    background: "linear-gradient(90deg, transparent, rgba(54,196,168,0.25), transparent)",
    animation: "scanLine 6s linear infinite",
    zIndex: 3,
    pointerEvents: "none",
  },

  telemTL: {
    position: "fixed",
    top: 36,
    left: 64,
    zIndex: 4,
    fontFamily: "var(--font-mono)",
    fontSize: "9px",
    letterSpacing: "0.28em",
    textTransform: "uppercase",
    color: PALETTE.teal700,
    lineHeight: 1.9,
  },

  telemBottom: {
    position: "fixed",
    bottom: 28,
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 4,
    fontFamily: "var(--font-mono)",
    fontSize: "9px",
    letterSpacing: "0.28em",
    textTransform: "uppercase",
    color: "rgba(36,130,119,0.4)",
    whiteSpace: "nowrap",
  },

  deco: {
    position: "fixed",
    top: "12%",
    right: "5%",
    width: "220px",
    height: "220px",
    zIndex: 2,
    opacity: 0.06,
    pointerEvents: "none",
  },

  card: {
    position: "relative",
    zIndex: 5,
    width: "min(600px, 100%)",
    background: "linear-gradient(160deg, #0f2018 0%, #0a1a16 100%)",
    border: "1px solid rgba(53,143,128,0.22)",
    padding: "44px 40px 36px",
    boxShadow: "0 0 60px rgba(0,0,0,0.6), 0 0 0 1px rgba(54,196,168,0.04)",
  },

  cardTopLine: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "1px",
    background: "linear-gradient(90deg, transparent, rgba(54,196,168,0.5), transparent)",
  },

  eyebrow: {
    fontFamily: "var(--font-mono)",
    fontSize: "9px",
    letterSpacing: "0.32em",
    textTransform: "uppercase",
    color: PALETTE.teal700,
    marginBottom: 24,
  },

  h1: {
    fontFamily: "var(--font-display)",
    fontWeight: 300,
    fontSize: "clamp(38px, 5.5vw, 54px)",
    lineHeight: 0.95,
    letterSpacing: "-0.02em",
    color: PALETTE.white,
    marginBottom: 14,
  },

  lede: {
    fontFamily: "var(--font-body)",
    fontWeight: 300,
    fontSize: "14px",
    lineHeight: 1.75,
    color: PALETTE.whiteSoft,
    marginBottom: 24,
    maxWidth: 480,
  },

  divider: {
    height: "1px",
    background: "linear-gradient(90deg, rgba(53,143,128,0.25), transparent)",
    marginBottom: 24,
  },

  row: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 14,
    marginBottom: 16,
  },

  label: {
    display: "block",
    fontFamily: "var(--font-mono)",
    fontSize: "9px",
    letterSpacing: "0.25em",
    textTransform: "uppercase",
    color: PALETTE.teal700,
    marginBottom: 7,
  },

  input: {
    width: "100%",
    background: "rgba(10,18,16,0.6)",
    border: "1px solid rgba(53,143,128,0.25)",
    padding: "11px 14px",
    fontFamily: "var(--font-body)",
    fontSize: "14px",
    color: PALETTE.white,
    outline: "none",
    transition: `border-color ${MOTION.fast} ease`,
    caretColor: PALETTE.teal400,
    /* date input color fix */
    colorScheme: "dark",
  },

  err: {
    fontFamily: "var(--font-mono)",
    fontSize: "10px",
    letterSpacing: "0.18em",
    color: "#e07060",
    marginTop: 18,
    lineHeight: 1.5,
  },

  btn: {
    width: "100%",
    marginTop: 28,
    padding: "14px 20px",
    fontFamily: "var(--font-mono)",
    fontSize: "11px",
    letterSpacing: "0.18em",
    textTransform: "uppercase",
    background: "rgba(54,196,168,0.06)",
    color: PALETTE.white,
    border: "1px solid rgba(53,143,128,0.3)",
    cursor: "pointer",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    transition: `all ${MOTION.fast} ${MOTION.ease}`,
  },

  altRow: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: 22,
    fontFamily: "var(--font-mono)",
    fontSize: "10px",
    letterSpacing: "0.18em",
  },

  altLink: {
    color: PALETTE.teal700,
    textDecoration: "none",
    transition: `color ${MOTION.fast} ease`,
  },

  cardFooter: {
    fontFamily: "var(--font-mono)",
    fontSize: "8px",
    letterSpacing: "0.28em",
    textTransform: "uppercase",
    color: "rgba(36,130,119,0.35)",
    marginTop: 32,
    paddingTop: 20,
    borderTop: "1px solid rgba(53,143,128,0.1)",
  },
};
